import React from 'react';
import { toast } from 'react-toastify';
import { Add16Regular, ChevronDown16Regular, Copy16Regular } from '@fluentui/react-icons';
import styles from '../css/vdr.module.css';
import { SearchField, StatusDot, StatusFilters, normalizeStatus } from '../components/StatusUI';
const TranscriptStatus = ({ status }) => {
    const isReady = status === 'ready';
    return (
        <span className={isReady ? styles.transcriptStatusReady : styles.transcriptStatusMuted}>
            {isReady ? 'Ready' : 'None'}
        </span>
    );
};

const copyValue = async (value, label) => {
    try {
        await navigator.clipboard.writeText(value);
        toast.success(`${label} copied`);
    } catch {
        toast.info(value);
    }
};

const maskSecret = value => {
    const text = String(value || '');
    if (!text) return '';
    if (text.length <= 4) return text;
    return `${'\u2022'.repeat(8)}${text.slice(-4)}`;
};

const PlaudWebhookMenu = ({ webhook, onRotate }) => {
    const [open, setOpen] = React.useState(false);
    const [rotating, setRotating] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (!open) return undefined;
        const onDoc = event => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };
        const onKey = event => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const rotate = async () => {
        if (rotating) return;
        setRotating(true);
        try {
            await onRotate?.();
        } finally {
            setRotating(false);
        }
    };

    return (
        <div className={styles.plaudMenu} ref={ref}>
            <button
                type="button"
                className={[styles.plaudChip, open ? styles.plaudChipOpen : ''].filter(Boolean).join(' ')}
                aria-expanded={open}
                aria-haspopup="dialog"
                onClick={() => setOpen(value => !value)}
            >
                <span className={styles.plaudChipDot} aria-hidden="true" />
                Plaud webhook
                <ChevronDown16Regular className={styles.plaudChipChevron} />
            </button>
            {open ? (
                <div className={styles.plaudPopover} role="dialog" aria-label="Plaud webhook">
                    <div className={styles.plaudPopoverHead}>
                        <span className={styles.plaudPopoverTitle}>Plaud webhook</span>
                        <span className={styles.plaudConnected}>Connected</span>
                    </div>
                    <p className={styles.plaudPopoverHint}>Zapier POSTs new recordings to this URL.</p>
                    <div className={styles.plaudFieldBlock}>
                        <div className={styles.plaudFieldLabelRow}>
                            <span className={styles.plaudFieldLabel}>URL</span>
                        </div>
                        <div className={styles.plaudField}>
                            <span className={styles.plaudFieldValue} title={webhook.url}>
                                {webhook.url}
                            </span>
                            <button
                                type="button"
                                className={styles.mailboxCopyBtn}
                                aria-label="Copy URL"
                                onClick={() => copyValue(webhook.url, 'URL')}
                            >
                                <Copy16Regular /> Copy
                            </button>
                        </div>
                    </div>
                    <div className={styles.plaudFieldBlock}>
                        <div className={styles.plaudFieldLabelRow}>
                            <span className={styles.plaudFieldLabel}>Token</span>
                            {webhook.header ? <span className={styles.plaudHeaderChip}>{webhook.header}</span> : null}
                        </div>
                        <div className={styles.plaudField}>
                            <span className={styles.plaudFieldValue}>{maskSecret(webhook.token)}</span>
                            <button
                                type="button"
                                className={styles.mailboxCopyBtn}
                                aria-label="Copy token"
                                onClick={() => copyValue(webhook.token, 'Token')}
                                disabled={!webhook.token}
                            >
                                <Copy16Regular /> Copy
                            </button>
                        </div>
                    </div>
                    <div className={styles.plaudPopoverFooter}>
                        <button type="button" className={styles.plaudRotateBtn} onClick={rotate} disabled={rotating}>
                            {rotating ? 'Rotating…' : 'Rotate'}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const MeetingRecordingsView = ({
    recordings = [],
    onSelect,
    selectedId,
    onUpload,
    plaudWebhook = null,
    onRotatePlaudWebhook,
}) => {
    const [search, setSearch] = React.useState('');
    const [filter, setFilter] = React.useState('all');

    const filtered = recordings.filter(r => {
        const status = normalizeStatus(r.status);
        if (filter !== 'all' && status !== filter) return false;
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (
            r.title?.toLowerCase().includes(q) ||
            r.source?.toLowerCase().includes(q) ||
            (r.participantNames || []).some(n => n.toLowerCase().includes(q))
        );
    });

    return (
        <>
            <div className={styles.contentHeader}>
                <div>
                    <h2 className={styles.contentTitle}>Meeting Recordings</h2>
                    <p className={styles.contentDesc}>
                        Indexed transcripts are available as Ask Lens evidence. Audio recordings are not indexed.
                    </p>
                </div>
                <div className={styles.headerActions}>
                    {plaudWebhook?.url ? (
                        <PlaudWebhookMenu webhook={plaudWebhook} onRotate={onRotatePlaudWebhook} />
                    ) : null}
                    <button type="button" className={styles.primaryBtn} onClick={() => onUpload?.()}>
                        <Add16Regular /> Upload recording
                    </button>
                </div>
            </div>

            <div className={styles.toolbar}>
                <SearchField value={search} onChange={setSearch} placeholder="Search recordings" />
                <StatusFilters value={filter} onChange={setFilter} />
            </div>

            {filtered.length === 0 ? (
                <div className={styles.emptyState}>No recordings match your filters.</div>
            ) : (
                <div className={styles.table}>
                    <div className={`${styles.tableHead} ${styles.recordingCols}`}>
                        <span>Recording</span>
                        <span>Meeting date</span>
                        <span>Duration</span>
                        <span>Source</span>
                        <span>Transcript</span>
                        <span>Lens status</span>
                    </div>
                    {filtered.map(rec => (
                        <div
                            key={rec.id}
                            className={`${styles.tableRow} ${styles.recordingCols} ${
                                selectedId === rec.id ? styles.tableRowSelected : ''
                            }`}
                            onClick={() => onSelect?.(rec)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') onSelect?.(rec);
                            }}
                        >
                            <div>
                                <div className={styles.tableRowName}>
                                    <span>{rec.title}</span>
                                </div>
                                <div className={styles.subMeta}>{rec.participants || 0} participants</div>
                            </div>
                            <span className={styles.cellMuted}>{rec.meetingDate}</span>
                            <span className={styles.cellMuted}>{rec.duration}</span>
                            <span>
                                <span className={styles.sourcePill}>{rec.source}</span>
                            </span>
                            <TranscriptStatus status={rec.transcriptStatus} />
                            <StatusDot status={rec.status} />
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default MeetingRecordingsView;
